import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import FreeConsultationCTA from './FreeConsultationCTA';

const WhyBytePairs = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  const valuePropositions = [
    {
      id: 'personal',
      icon: 'UserCheck',
      title: 'Personal Attention & Collaboration',
      subtitle: 'Work directly with the builders, not account managers',
      description: `We are a small, dedicated startup team. When you partner with us, you communicate directly with the developers writing your code. We keep communication loops fast, transparent, and completely free of corporate translation layers.`,
      features: [
        'Direct developer messaging channels',
        'Weekly video syncs and progress reviews',
        'Flexible project pivots as requirements evolve',
        'Direct strategic consultations'
      ],
      stats: { metric: 'Direct', label: 'Founder-to-Client Access' },
      color: 'from-blue-500 to-cyan-500',
      bgPattern: 'bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.1),transparent_50%)]'
    },
    {
      id: 'custom',
      icon: 'Layers',
      title: 'Custom & Scalable Solutions',
      subtitle: 'Zero software bloat, built strictly for your goals',
      description: `We build digital solutions tailored precisely to your operational needs. Whether it is clinic booking management, retail stock tracking, or workflow automation, we write clean code that scales easily as your transactions grow—without forcing you into expensive, rigid SaaS plans.`,
      features: [
        'Clean React, Node, and Python codebase',
        'Data ownership is 100% yours',
        'Modular architecture ready for future upgrades',
        'Integrated database configurations'
      ],
      stats: { metric: 'Zero', label: 'Unnecessary Bloated Features' },
      color: 'from-green-500 to-emerald-500',
      bgPattern: 'bg-[radial-gradient(circle_at_70%_30%,rgba(16,185,129,0.1),transparent_50%)]'
    },
    {
      id: 'pricing',
      icon: 'IndianRupee',
      title: 'Affordable, Transparent Pricing',
      subtitle: 'Premium engineering at startup-friendly prices',
      description: `Exceptional software should not be exclusive to massive budgets. By maintaining a lean team and choosing efficient, modern technologies, we deliver premium-quality systems at fair prices with complete billing transparency.`,
      features: [
        'Detailed, itemized cost proposals',
        'Zero hidden hosting or setup fees',
        'One-time builds with optional maintenance plans',
        'High return on software investment'
      ],
      stats: { metric: 'Fair', label: 'One-Time Fees & Honest Quotes' },
      color: 'from-purple-500 to-pink-500',
      bgPattern: 'bg-[radial-gradient(circle_at_50%_80%,rgba(168,85,247,0.1),transparent_50%)]'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      {/* Background Elements */}
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{ y, opacity }}
      >
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-accent/10 rounded-full blur-xl"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Why Choose <span className="text-gradient">Byte Pairs</span>?
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            We are not a massive corporate agency. We are a direct, skilled technical partner 
            focused on building clean digital solutions to solve your specific operational challenges.
          </p>
        </motion.div>

        {/* Value Propositions */}
        <motion.div
          className="space-y-20"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {valuePropositions.map((proposition, index) => (
            <motion.div
              key={proposition.id}
              variants={itemVariants}
              className={`relative ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} flex flex-col lg:flex-row items-center gap-12`}
            >
              {/* Background Pattern */}
              <div className={`absolute inset-0 ${proposition.bgPattern} rounded-3xl`}></div>

              {/* Content Side */}
              <div className="flex-1 relative z-10">
                <motion.div
                  className="mb-6"
                  whileInView={{ scale: [0.8, 1.1, 1] }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${proposition.color} rounded-2xl flex items-center justify-center mb-4 shadow-brand`}>
                    <Icon name={proposition.icon} size={32} color="white" />
                  </div>
                </motion.div>

                <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                  {proposition.title}
                </h3>
                <p className="text-lg text-primary font-medium mb-6">
                  {proposition.subtitle}
                </p>
                <p className="text-text-secondary text-lg leading-relaxed mb-8">
                  {proposition.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {proposition.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      className="flex items-center space-x-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: featureIndex * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className="w-5 h-5 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Stats */}
                <motion.div
                  className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-6 inline-block shadow-brand"
                  whileInView={{ scale: [0.9, 1.05, 1] }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-center">
                    <div className={`text-4xl font-bold bg-gradient-to-r ${proposition.color} bg-clip-text text-transparent mb-2`}>
                      {proposition.stats.metric}
                    </div>
                    <div className="text-text-secondary text-sm font-medium">
                      {proposition.stats.label}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Visual Side */}
              <div className="flex-1 relative">
                <motion.div
                  className="relative"
                  whileInView={{
                    rotateY: [0, 5, 0],
                    scale: [0.95, 1.02, 1]
                  }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                  viewport={{ once: true }}
                >
                  {/* Main Visual Card */}
                  <div className="bg-card border border-border rounded-2xl p-8 shadow-brand-lg relative overflow-hidden">
                    {/* Animated Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${proposition.color} opacity-5`}></div>

                    {/* Visual Card Dynamic Content based on Prop ID */}
                    <div className="relative z-10">
                      {proposition.id === 'personal' && (
                        <div className="font-sans text-xs">
                          <div className="flex items-center justify-between pb-4 border-b border-border/60 mb-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">
                                BP
                              </div>
                              <div>
                                <div className="text-xs font-bold text-foreground">Direct Developer Chat</div>
                                <div className="flex items-center space-x-1">
                                  <span className="w-1.5 h-1.5 bg-success rounded-full animate-ping"></span>
                                  <span className="text-[10px] text-text-secondary">Giri (Co-Founder) is active</span>
                                </div>
                              </div>
                            </div>
                            <Icon name="MessageSquare" size={14} className="text-primary" />
                          </div>
                          
                          <div className="space-y-3 text-xs text-left">
                            <div className="bg-muted/70 p-3 rounded-2xl rounded-tl-none max-w-[85%] border border-border/30">
                              <p className="text-text-secondary text-[9px] mb-1 font-semibold uppercase">You (Client)</p>
                              <p className="text-foreground leading-relaxed">Hi Giri, can we add automatic WhatsApp alerts when a booking is confirmed?</p>
                            </div>
                            
                            <div className="bg-primary/10 p-3 rounded-2xl rounded-tr-none max-w-[85%] ml-auto border border-primary/20">
                              <p className="text-primary text-[9px] mb-1 font-semibold uppercase">Giri (Developer)</p>
                              <p className="text-foreground leading-relaxed">Yes, absolutely! I am writing the API trigger logic now. We can test it tomorrow morning.</p>
                            </div>

                            <div className="bg-muted/70 p-3 rounded-2xl rounded-tl-none max-w-[85%] border border-border/30">
                              <p className="text-text-secondary text-[9px] mb-1 font-semibold uppercase">You (Client)</p>
                              <p className="text-foreground leading-relaxed">Perfect, thank you! It's great speaking directly to the builder.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {proposition.id === 'custom' && (
                        <div className="font-sans text-xs text-left">
                          <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-4">
                            <span className="font-bold text-foreground">Clinic Queue Dashboard</span>
                            <span className="text-[10px] bg-success/15 text-success px-2 py-0.5 rounded-full font-bold">System Online</span>
                          </div>

                          <div className="space-y-2.5">
                            <div className="flex items-center justify-between bg-muted/50 p-2.5 rounded-xl border border-border/40">
                              <div className="flex items-center space-x-2">
                                <Icon name="Clock" size={12} className="text-primary" />
                                <span className="font-semibold text-foreground">Dr. Liyon - 10:30 AM Slot</span>
                              </div>
                              <span className="bg-primary/20 text-primary text-[10px] px-2 py-0.5 rounded font-bold">In Progress</span>
                            </div>

                            <div className="flex items-center justify-between bg-muted/50 p-2.5 rounded-xl border border-border/40">
                              <div className="flex items-center space-x-2">
                                <Icon name="AlertTriangle" size={12} className="text-warning" />
                                <span className="font-semibold text-foreground">Low Stock: Bed Sheets (4 left)</span>
                              </div>
                              <span className="bg-warning/20 text-warning text-[10px] px-2 py-0.5 rounded font-bold">Alert Sent</span>
                            </div>

                            <div className="flex items-center justify-between bg-muted/50 p-2.5 rounded-xl border border-border/40">
                              <div className="flex items-center space-x-2">
                                <Icon name="CheckCircle" size={12} className="text-success" />
                                <span className="font-semibold text-foreground">Invoicing #1094 WhatsApp Log</span>
                              </div>
                              <span className="bg-success/20 text-success text-[10px] px-2 py-0.5 rounded font-bold">Auto-Sent</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {proposition.id === 'pricing' && (
                        <div className="font-sans text-xs text-left">
                          <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-4">
                            <div>
                              <span className="font-bold text-foreground block">Itemized Project Scope</span>
                              <span className="text-[10px] text-text-secondary">Quote #2026-BP</span>
                            </div>
                            <span className="text-primary font-bold text-xs uppercase bg-primary/10 px-2 py-0.5 rounded">One-Time Cost</span>
                          </div>

                          <div className="space-y-2.5">
                            <div className="flex justify-between items-center text-text-secondary">
                              <span className="flex items-center"><Icon name="Check" size={14} className="text-success mr-2" /> Custom CRM & Invoicing</span>
                              <span className="font-bold text-foreground">₹50,000</span>
                            </div>
                            <div className="flex justify-between items-center text-text-secondary">
                              <span className="flex items-center"><Icon name="Check" size={14} className="text-success mr-2" /> WhatsApp Alerts Trigger</span>
                              <span className="font-bold text-foreground">₹15,000</span>
                            </div>
                            <div className="flex justify-between items-center text-text-secondary">
                              <span className="flex items-center"><Icon name="Check" size={14} className="text-success mr-2" /> Google Business Map Setup</span>
                              <span className="font-bold text-success">Included</span>
                            </div>
                            <div className="flex justify-between items-center text-text-secondary">
                              <span className="flex items-center"><Icon name="Check" size={14} className="text-success mr-2" /> 3-Month Developer Support</span>
                              <span className="font-bold text-success">Free</span>
                            </div>

                            <div className="pt-2.5 border-t border-border/60 flex justify-between items-center font-bold text-foreground">
                              <span>Total Investment</span>
                              <span className="text-primary text-sm">₹65,000</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Floating Elements */}
                    <motion.div
                      className="absolute -top-3 -right-3 w-6 h-6 bg-primary rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <motion.div
                      className="absolute -bottom-2 -left-2 w-4 h-4 bg-accent rounded-full"
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 1, 0.5]
                      }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Free Consultation Form Section */}
        <FreeConsultationCTA />
      </div>
    </section>
  );
};

export default WhyBytePairs;
